import {
  createFileRoute,
  Outlet,
  redirect,
  useChildMatches,
  useNavigate,
} from '@tanstack/react-router'
import type { PurchaseOrderFilters, PurchaseOrderStatus } from '@/features/purchasing-orders/api'
import { OrderList } from '@/features/purchasing-orders/order-list'
import { hasGrant } from '@/lib/me'

/**
 * Route shell for the purchase order list.
 *
 * `orders.$orderId.index.tsx` and `orders.$orderId.receive.tsx` are CHILDREN
 * of this route in flat file routing (mirrors
 * `_authenticated/accounting/bills.tsx`): without the Outlet below, their
 * URLs render this list instead of themselves (apps/company/AGENTS.md).
 */
export const Route = createFileRoute('/_authenticated/purchasing/orders')({
  validateSearch: (search: Record<string, unknown>): PurchaseOrderFilters => ({
    status: isStatus(search.status) ? search.status : undefined,
    supplier: typeof search.supplier === 'string' && search.supplier ? search.supplier : undefined,
    warehouse:
      typeof search.warehouse === 'string' && search.warehouse ? search.warehouse : undefined,
    from: typeof search.from === 'string' ? search.from : undefined,
    to: typeof search.to === 'string' ? search.to : undefined,
    search: typeof search.search === 'string' && search.search ? search.search : undefined,
    sort: isSort(search.sort) ? search.sort : undefined,
  }),
  beforeLoad: ({ context }) => {
    if (!hasGrant(context.queryClient, 'purchase_order.view')) {
      throw redirect({ to: '/' })
    }
  },
  component: PurchasingOrdersRoute,
})

function PurchasingOrdersRoute() {
  const navigate = useNavigate()
  const children = useChildMatches()
  const params = Route.useSearch()

  if (children.length > 0) {
    return <Outlet />
  }

  return (
    <OrderList
      params={params}
      onParamsChange={(next) => void navigate({ to: '/purchasing/orders', search: next })}
    />
  )
}

const STATUSES: PurchaseOrderStatus[] = [
  'draft',
  'submitted',
  'approved',
  'partially_received',
  'received',
  'cancelled',
]

function isStatus(value: unknown): value is PurchaseOrderStatus {
  return typeof value === 'string' && (STATUSES as string[]).includes(value)
}

const SORTS: NonNullable<PurchaseOrderFilters['sort']>[] = [
  'order_date',
  '-order_date',
  'expected_date',
  '-expected_date',
  'total_amount',
  '-total_amount',
]

function isSort(value: unknown): value is NonNullable<PurchaseOrderFilters['sort']> {
  return typeof value === 'string' && (SORTS as string[]).includes(value)
}
